import React, { useState, createRef, useEffect } from 'react';
import { Map, GeoJSON } from 'react-leaflet';
import phuBoundaries from '../../LHIN.json';
import dict from '../../dictionary';
import cData from '../../reducedData.json';
import FuzySearch from '../fuzySearch/fuzySearch';
import { getPopupContent } from './Popup';
import { lerp, isVowelFr } from '../utils';
import 'leaflet/dist/leaflet.css';
import './hospitalizationMap.scss';
import HeatmapLegend from './HeatmapLegend';
import useInterval from './useInterval';
import trans from '../../translations.json';
import Loading from '../loading/loading';
import { highlightSearchResult } from './highlightSearchResult';
import ChartMetric from './ChartMetric';
import dictionary from '../../dictionary';
import SearchResultsDropdown from '../search/searchResultDropdown';

const getLHINName = (phu_name) => {
  if (dict.North_region.includes(phu_name)) {
    return 'North';
  } else if (dict.Central_region.includes(phu_name)) {
    return 'Central';
  } else if (dict.East_region.includes(phu_name)) {
    return 'East';
  } else if (dict.Torronto_region.includes(phu_name)) {
    return 'Toronto';
  } else {
    return 'West';
  }
};

const citiesLHIN = Object.keys(cData.dataPHU).reduce(
  (sum, currPHU) => {
    return sum.concat(
      cData.dataPHU[currPHU].cities.map((city) => {
        return {
          city_name: city,
          lhin_name: getLHINName(
            cData.dataPHU[currPHU][dict.Reporting_PHU],
          ),
        };
      }),
    );
  },
  [],
);

// const highestPhu = Object.values({ ...cData.dataPHU }).sort(
//   (a, b) => b[dict.PHU_pop] - a[dict.PHU_pop],
// )[0];

//console.log('highestPhu', highestPhu);

//Fsa disabled
const fsaArr = false;
// const fsaArr = Object.keys(cData.dataFSA).reduce((sum, currFsa) => {
//   if (
//     currFsa === 'maxTotal' ||
//     currFsa === 'maxCapita' ||
//     currFsa === 'Unknown FSA'
//   ) {
//     return sum;
//   }

//   return sum.concat([{ [dict.FSA_name]: currFsa }]);
// }, []);

const defaultMapCfg = {
  lat: 44.61,
  lng: -79.42,
  zoom: 6,
};

const mapColors = {
  [dict.hospitalized]: {
    start: [234, 202, 171],
    end: [182, 104, 42],
  },
  [dict.inICU]: {
    start: [228, 190, 188],
    end: [164, 73, 67],
  },
  [dict.bedsTaken]: {
    start: [184, 217, 180],
    end: [79, 140, 71],
  },
};

const searchOpts = {
  [dict.phu]: {
    includeMatches: true,
    keys: ['city_name'],
  },
  [dict.fsa]: {
    includeMatches: true,
    keys: [dict.FSA_name],
    findAllMatches: true,
  },
};
const HospitalizationMap = (props) => {
  /**
   * A bit of badly written terminology used in this component
   * =====================
   * isPerCapita -> true/false. Whether the map is in a /100k view or not.
   *
   * metric -> dict.NotResolved/dict.resolved/dict.deaths/dict.totaCases. What the map measures the virus by.
   *
   * mode -> dict.phu/dict.fsa. Set of geometric polygons describing the area of effect.
   */

  // keep track of how to display and calculate the map
  const [isPerCapita, setIsPerCapita] = useState(false);
  const [metric, setMetric] = useState(dict.hospitalized);
  const [mode, setMode] = useState(dict.phu); // either phu or fsa

  // gatekeepers that clean input before actually sending it to their actual values
  const [
    intentionToTransferMode,
    setIntentionToTransferMode,
  ] = useState();
  const [
    intentionToTransferMetric,
    setIntentionToTransferMetric,
  ] = useState(dict.hospitalized);

  // store geojson bounds for both maps
  const [bounds, setBounds] = useState({});
  const [postalBounds, setPostalBounds] = useState({});

  // if the fsa geojson has finished downloading
  const [fsaReady, setFsaReady] = useState(false);

  // display
  const [mapColor, setMapColor] = useState(
    mapColors[dict.hospitalized],
  );
  const [showCallout, setShowCallout] = useState(false);

  // search
  const [searchResults, setSearchResults] = useState([]);
  const [waitingForSearch, setWaitingForSearch] = useState(false);

  // overwriting fuzzy search query
  const [searchQuery, setSearchQuery] = useState('');
  const [searchQueryTime, setSearchQueryTime] = useState(+new Date());

  // leaflet
  const mapRef = createRef();

  const resetMapView = () => {
    mapRef.current.leafletElement.flyTo(
      [defaultMapCfg.lat, defaultMapCfg.lng],
      defaultMapCfg.zoom,
      { animate: true, duration: 1 },
    );
  };
  /**
   * Gets the shade that a phu feature should be filled with based on current state.
   * @param {string} featureName Full name of Feature (phu/fsa)
   */
  const getShade = (featureName) => {
    if (featureName) {
      let perc;
      const capitaMode = isPerCapita ? 'capita' : 'total';
      if (mode === dict.phu) {
        // PHU
        const lhinData = cData.dataLHIN[featureName.toUpperCase()];
        // console.log('cData', cData);
        perc = lhinData[metric]
          ? lhinData[metric] /
            cData.dataLHIN.maxStats[capitaMode][metric]
          : [];

        //console.log('capitaMode', capitaMode);

        if (isPerCapita) {
          perc /= dict.LHIN_population[featureName.toUpperCase()];
        }
      } else {
        // FSA
        const fsaData = cData.dataFSA[featureName];

        try {
          perc =
            fsaData[
              isPerCapita ? dict.FSA_per_100k : dict.FSA_active
            ] / cData.maxStats[dict.fsa][capitaMode][metric];
        } catch (err) {
          perc = 0;
        }
      }

      return `rgb(${lerp(
        mapColor.start,
        mapColor.end,
        perc,
      ).toString()})`;
    }
  };
  const onClickSearchResult = (phu_name) => {
    setSearchResults([]);
    setSearchQuery(phu_name);
    setSearchQueryTime(+Date.now());
    selectZone(phu_name);
  };
  const hideDropdown = () => {
    setSearchResults([]);
  };
  //sync colors
  useEffect(() => {
    setMapColor(mapColors[metric]);
  }, [metric]);

  useEffect(() => {
    if (
      intentionToTransferMetric !== dict.NotResolved &&
      (intentionToTransferMode === dict.fsa || mode === dict.fsa)
    ) {
      // redirect to phu mode
      setMode(dict.phu);
      setMetric(intentionToTransferMetric);

      resetMapView();

      // clear search in fuzzysearch to prevent loop
      setSearchQuery('');
      setSearchQueryTime(+new Date());

      // since search got cleared, remove results as well
      setSearchResults([]);

      //show callout
      setShowCallout(true);
    } else {
      setShowCallout(false);
      if (intentionToTransferMetric)
        setMetric(intentionToTransferMetric);
    }
  }, [intentionToTransferMetric, intentionToTransferMode]);

  const geoJSONStyle = (feature) => {
    let featureName;

    if (mode === dict.phu) {
      featureName = feature.properties[dict.REGION_NAME];
    } else {
      featureName = feature.properties[dict.FSA_ID];
    }
    const config = {
      color: '#fff',
      weight: 1,
      fillOpacity: 1,
      fillColor: getShade(featureName),
    };

    return config;
  };

  // dynamically change the popup content based on if it is per capita or not
  useEffect(() => {
    if (mode === dict.phu) {
      Object.keys(bounds).map((phu) => {
        bounds[phu].setPopupContent(
          getPopupContent(phu, props.lang, mode, isPerCapita),
        );
      });
    }
  }, [isPerCapita]);

  // if trying to transfer to fsa, but the data has not been downloaded,
  // keep polling until it's possible
  useInterval(
    () => {
      if (intentionToTransferMode === dict.fsa && !fsaReady) {
        return;
      } else {
        setMode(intentionToTransferMode);
        setIntentionToTransferMode();
      }
    },
    intentionToTransferMode ? 500 : null,
  );

  /**
   * Runs for every feature on the map
   */
  const onEachFeature = (feature, layer) => {
    let featureName;

    if (mode === dict.phu) {
      featureName = feature.properties[dict.REGION_NAME];
    } else {
      featureName = feature.properties[dict.FSA_ID];
    }

    // append the bounds of the phu to state
    if (mode === dict.phu) {
      setBounds((oldBounds) => {
        return { ...oldBounds, [featureName]: layer };
      });
    } else {
      setPostalBounds((oldBounds) => {
        return { ...oldBounds, [featureName]: layer };
      });
    }

    const popupContent = getPopupContent(
      featureName,
      props.lang,
      mode,
      isPerCapita,
    );

    layer.bindPopup(popupContent);
  };

  /**
   * Zooms in and map autofits to a GEOJSON object
   * @param {string} featureName PHU to zoom to
   */
  const selectZone = (featureName) => {
    const map = mapRef.current.leafletElement;

    const fitTo =
      mode === dict.phu
        ? bounds[featureName]
        : postalBounds[featureName];
    map.fitBounds(fitTo.getBounds());
    setTimeout(() => {
      fitTo.openPopup();
    }, 500);
  };

  /**
   * Sets search data state after Fuze returns a new search
   */
  const searchResultsCallback = (query, results) => {
    if (query.length < 3) {
      if (searchResults !== []) {
        setSearchResults([]);
      }
      return;
    }

    setWaitingForSearch(false);

    setSearchResults(() => {
      return results
        ? results.slice(0, Math.min(results.length, 3))
        : [];
    });
  };

  const SearchResultsDropdown2 = () => {
    const onMouseDown = (item) => {
      const featureName =
        mode === dict.phu
          ? item.item.lhin_name
          : item.item[dict.FSA_name];

      // remove dropdown
      setSearchResults([]);

      // set map to zoom to zone
      selectZone(featureName);

      // push the featurename to fuzzy search
      setSearchQuery(featureName);
      setSearchQueryTime(+new Date());
    };

    const formatResult =
      mode === dict.phu
        ? (item) => {
            const phuName = item.item.lhin_name;
            const cityName = item.item.city_name;
            const connector =
              props.lang === 'en'
                ? 'in '
                : isVowelFr(cityName.charAt(0))
                ? 'en'
                : 'en ';

            return (
              <>
                {cityName} {connector}
                <b>
                  {highlightSearchResult(
                    phuName,
                    item.matches[0].indices,
                  )}
                </b>
              </>
            );
          }
        : (item) => {
            return (
              <b style={{ width: '100%' }}>
                {' '}
                {item.item[dict.FSA_name]}
              </b>
            );
          };

    return (
      (waitingForSearch || searchResults.length !== 0) && (
        <ul class="viz-search-result-dropdown" role="toolbar">
          {waitingForSearch ? (
            <li className="search-loader-container">
              <div className="loader search-loader" />{' '}
              <span>Loading...</span>
            </li>
          ) : (
            searchResults.length !== 0 &&
            searchResults.map((item, idx) => {
              return (
                <li>
                  <button
                    aria-setsize="4"
                    aria-posinset={idx}
                    onMouseDown={() => {
                      onMouseDown(item);
                    }}
                    key={idx}
                    style={{ cursor: 'pointer' }}
                  >
                    {formatResult(item)}
                  </button>
                </li>
              );
            })
          )}
        </ul>
      )
    );
  };

  let message = '';
  function getMessage(metric) {
    if (metric === dict.hospitalized) {
      message = trans.heatmap.searchLhinHospital[props.lang];
    } else if (metric === dict.inICU) {
      message = trans.heatmap.searchLhinIcu[props.lang];
    } else if (metric === dict.bedsTaken) {
      message = trans.heatmap.searchLhinVent[props.lang];
    }

    return <p dangerouslySetInnerHTML={{ __html: message }}></p>;
  }

  return (
    <div
      id="viz-region-heatmap"
      className="ontario-row ontario-margin-bottom-32-!"
    >
      {props.showSidebar ? (
        <div
          id="compare-chart-controls"
          className="ontario-columns ontario-medium-4 ontario-small-12 ontario-margin-bottom-8-!"
        >
          {!props.hideChartMetric && (
            <ChartMetric
              lang={props.lang}
              setMetric={setIntentionToTransferMetric}
              metric={metric}
              mode={mode}
            />
          )}

          {/* citiesPHU.length && fsaArr.length */}

          {citiesLHIN.length && (
            <>
              <div className="ontario-margin-bottom-8-!">
                <div className="ontario-label ontario-margin-bottom-8-!">
                  {!props.fuzzySearchProp
                    ? (metric === dict.NotResolved
                        ? trans.heatmap.showByPhuOrPostal
                        : trans.heatmap.showByPhu)[props.lang]
                    : props.fuzzySearchProp.title}
                </div>
                {!props.fuzzySearchProp && (
                  <div className="ontario-margin-bottom-32-!">
                    {getMessage(metric)}
                    {/* {trans.overview.Enter[props.lang]}{' '}
                    {trans.heatmap.firstBullet[props.lang]} */}
                  </div>
                )}
              </div>
              <FuzySearch
                onBlur={() =>
                  setTimeout(() => setSearchResults([]), 200)
                }
                searchQuery={searchQuery}
                searchQueryTime={searchQueryTime}
                searchResultsCallback={searchResultsCallback}
                data={mode === dict.phu ? citiesLHIN : fsaArr}
                minSearchLength={3}
                searchSettings={searchOpts[mode]}
                searchResultsStartCallback={() => {
                  setWaitingForSearch(true);
                }}
                searchLabel={trans.heatmap.city[props.lang]}
                class="ontario-label"
              />
            </>
          )}
          <SearchResultsDropdown
            lang={props.lang}
            results={searchResults}
            handleOnClick={onClickSearchResult}
            isHospitalization={true}
            hideDropdown={hideDropdown}
          />
        </div>
      ) : null}
      <div
        id="regionalMap"
        className="ontario-columns ontario-medium-8 ontario-small-12"
      >
        <Map
          aria-hidden="true"
          attributionControl={false}
          center={[defaultMapCfg.lat, defaultMapCfg.lng]}
          zoom={defaultMapCfg.zoom}
          minZoom={4}
          scrollWheelZoom={false}
          ref={mapRef}
          className="leaflet-container"
          closePopupOnClick={true}
          style={{ background: '#fff' }}
        >
          {mode === dict.phu && !intentionToTransferMode && (
            <GeoJSON
              data={phuBoundaries}
              style={geoJSONStyle}
              onEachFeature={onEachFeature}
            />
          )}

          {intentionToTransferMode && (
            <div className="covid-loader">
              <Loading />
            </div>
          )}

          {/* FSA Data */}
          {mode === dict.fsa && !intentionToTransferMode && (
            <GeoJSON
              data={postalBounds}
              style={geoJSONStyle}
              onEachFeature={onEachFeature}
            />
          )}
        </Map>
        <HeatmapLegend
          lang={props.lang}
          startColor={mapColor.start}
          endColor={mapColor.end}
          isPerCapita={isPerCapita}
          toPrecision={isPerCapita ? 2 : 2}
          maxNum={
            isPerCapita
              ? // cData.maxStats[mode]['capita'][metric].toFixed(2)
                cData.dataLHIN.maxStats['capita'][metric] * 100000
              : cData.dataLHIN.maxStats['total'][metric]

            // cData.maxStats[mode][isPerCapita ? 'capita' : 'total'][
            //   metric
            // ]
          }
        />
      </div>
    </div>
  );
};

export default HospitalizationMap;
